import { memo, useState, useCallback, lazy, Suspense, useId } from 'react';
import { useResume } from '@/contexts/resume-context';
import { YamlEditor } from './yaml-editor';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { showSuccessToast, showErrorToast } from '@/lib/toast';
import { FileText, Save, AlertCircle, Sparkles } from 'lucide-react';

const LazyAIConfigBar = lazy(() =>
  import('./ai-config-bar').then((module) => ({
    default: module.AIConfigBar,
  }))
);

function EditorPanelComponent() {
  const { yamlText, updateYaml, isValid, saveResume } = useResume();
  const [showConfig, setShowConfig] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const editorLabelId = useId();
  const editorDescriptionId = useId();

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      saveResume();
      showSuccessToast.resumeSaved();
    } catch (err) {
      showErrorToast.yamlParse('Failed to save resume');
    } finally {
      setIsSaving(false);
    }
  }, [saveResume]);

  return (
    <div className="h-full flex flex-col bg-df-surface">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-df-border bg-df-elevated/30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-df-accent-cyan/10 flex items-center justify-center">
            <FileText className="w-4 h-4 text-df-accent-cyan" />
          </div>
          <div>
            <h2 id={editorLabelId} className="text-sm font-medium text-df-text">YAML Editor</h2>
            <p className="text-xs text-df-text-muted">Edit your resume data</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Validation Status - Only shows when invalid */}
          {!isValid && (
            <div 
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium"
              style={{ 
                backgroundColor: 'rgba(255, 51, 102, 0.1)', 
                color: '#FF3366' 
              }}
            >
              <AlertCircle className="w-3.5 h-3.5" />
              Invalid
            </div>
          )}
          
          {/* Save Button */}
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-5 py-2.5 bg-df-elevated hover:bg-df-elevated-2 border border-df-border rounded-lg text-sm text-df-text transition-fluid disabled:opacity-50"
          >
            {isSaving ? (
              <LoadingSpinner size="sm" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Save</span>
          </button>
        </div>
      </div>

      {/* AI Config Toggle */}
      <div className="px-6 py-3 border-b border-df-border">
        <button
          type="button"
          onClick={() => setShowConfig(!showConfig)}
          className="flex items-center gap-2 text-sm text-df-text-secondary hover:text-df-text transition-fluid"
          aria-expanded={showConfig}
          aria-controls="ai-config-panel"
        >
          <Sparkles className="w-4 h-4 text-df-accent-purple" />
          <span>AI Configuration</span>
          <span className={`transform transition-transform ${showConfig ? 'rotate-180' : ''}`}>▼</span>
        </button>
      </div>

      {/* AI Config Panel */}
      {showConfig && (
        <div id="ai-config-panel" className="content-fade-in">
          <Suspense
            fallback={
              <div className="border-b border-df-border px-6 py-4">
                <LoadingSpinner size="sm" text="Loading AI configuration..." />
              </div>
            }
          >
            <LazyAIConfigBar />
          </Suspense>
        </div>
      )}

      {/* Editor */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <YamlEditor
          value={yamlText}
          onChange={updateYaml}
          error={!isValid}
          ariaLabel="Resume YAML editor"
          ariaLabelledBy={editorLabelId}
          ariaDescribedBy={editorDescriptionId}
        />
      </div>

      {/* Footer */}
      <div
        id={editorDescriptionId}
        className="px-6 py-3 border-t border-df-border bg-df-elevated/30 flex items-center justify-between text-xs text-df-text-muted"
      >
        <span>Use YAML format for structured data</span>
        <span className="font-mono">Ctrl+S to save</span>
      </div>
    </div>
  );
}

export const EditorPanel = memo(EditorPanelComponent);
